/**
 * Find the LC (Logistics Coordinator) filter option that represents the
 * signed-in user, so pages can default their LC filter to "my view".
 *
 * The LC options come from the backend `lc_query`: `value` is the V1 `csr`
 * short username (e.g. "jduplechain") and `label` is the person's full name
 * (e.g. "John Duplechain"). Crucially the short username has no dot, while
 * login emails are "first.last@domain" — so matching the option `value`
 * against the email local-part ("john.duplechain") fails for nearly every user
 * and silently falls back to All LCs.
 *
 * We therefore match the full-name `label` against the MSAL display name first
 * (this is what the Scheduling grid does, and it works), keeping the
 * value-vs-email check only as a defensive fallback for the rare account whose
 * csr username does equal its email local-part.
 */
export interface LcOption {
  value: string;
  label: string;
}

export interface CurrentUserAccount {
  /** MSAL account display name, e.g. "John Duplechain". */
  name?: string;
  /** MSAL account username — the login email, e.g. "john.duplechain@gwii.com". */
  username?: string;
}

export function matchLcToCurrentUser(
  options: readonly LcOption[],
  account: CurrentUserAccount | undefined,
): LcOption | undefined {
  if (!account) return undefined;

  const displayName = (account.name || '').trim().toLowerCase();
  const email = (account.username || '').trim().toLowerCase();
  const localPart = email.split('@')[0];

  // Primary: full name (label) === MSAL display name.
  if (displayName) {
    const byName = options.find((o) => o.label.trim().toLowerCase() === displayName);
    if (byName) return byName;
  }

  // Fallback: csr username (value) === email local-part or the full email.
  if (email) {
    const byValue = options.find((o) => {
      const v = o.value.trim().toLowerCase();
      return v === localPart || v === email;
    });
    if (byValue) return byValue;
  }

  return undefined;
}
